import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getApiUrl } from '../utils/api';
import {
  AUTH_SESSION_CHANGING_EVENT,
  AUTH_SESSION_INVALIDATION_STORAGE_KEY,
  AUTH_SESSION_INVALID_EVENT,
  AUTH_STORAGE_KEY,
  authSessionMatchesInvalidation,
  buildAuthSessionInvalidation,
  clearStoredSession,
  getStoredAuthSessionInvalidation,
  getStoredSession,
  invalidatePageAuthSession,
  isStoredSessionCurrent,
  parseAuthSessionInvalidation,
  pinPageAuthSession,
  publishAuthSessionInvalidation,
  setStoredSession,
  shouldIgnoreStoredSessionOnBootstrap,
  shouldIgnorePrimaryAuthOnBootstrap,
  shouldInvalidateForExternalAuthChange,
  shouldReloadForExternalAuthChange,
} from '../utils/auth';
import { purgeCatalogSessionStorage } from '../utils/catalogSessionStorage';

const API_URL = getApiUrl();
const AuthContext = createContext(null);

const announceSessionChanging = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_CHANGING_EVENT));
};

const hasKdsDeviceSession = () => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem('brutos-kds-device-session') || 'null'
    );
    return Boolean(stored?.token);
  } catch {
    return false;
  }
};

const handleExternalPrimarySessionChange = () => {
  if (typeof window === 'undefined') return;
  window.__brutosExternalAuthChanged = true;
  purgeCatalogSessionStorage();
  announceSessionChanging();

  if (shouldReloadForExternalAuthChange(window.location.pathname, {
    hasKdsDeviceSession: hasKdsDeviceSession(),
  })) {
    window.location.reload();
  }
};

const getBootstrapSession = () => {
  const storedSession = getStoredSession();
  const ignorePrimaryAuth = typeof window !== 'undefined' && (
    shouldIgnorePrimaryAuthOnBootstrap(window.location.pathname, {
      hasKdsDeviceSession: hasKdsDeviceSession(),
    })
    || shouldIgnoreStoredSessionOnBootstrap(
      window.location.pathname,
      window.location.search
    )
    || authSessionMatchesInvalidation(
      storedSession,
      getStoredAuthSessionInvalidation(window)
    )
  );
  if (
    ignorePrimaryAuth
  ) {
    pinPageAuthSession(null);
    return null;
  }
  return storedSession;
};

export const AuthProvider = ({ children }) => {
  const [bootstrapSession] = useState(() => getBootstrapSession());
  const [session, setSession] = useState(bootstrapSession);
  const [loading, setLoading] = useState(Boolean(bootstrapSession?.token));

  const terminatePrimarySession = useCallback((reason = 'session_invalid') => {
    announceSessionChanging();
    purgeCatalogSessionStorage();
    invalidatePageAuthSession();
    setSession(null);
    setLoading(false);

    if (shouldReloadForExternalAuthChange(window.location.pathname, {
      hasKdsDeviceSession: hasKdsDeviceSession(),
    })) {
      const loginReason = reason === 'payment_pending' ? 'payment_pending' : 'session_invalid';
      window.location.replace(`/login?reason=${loginReason}`);
    }
  }, []);

  useEffect(() => {
    const validateSession = async () => {
      const current = bootstrapSession;
      if (!current?.token) {
        setLoading(false);
        return;
      }
      if (!isStoredSessionCurrent(current)) {
        handleExternalPrimarySessionChange();
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${current.token}`,
          },
        });

        if (response.status === 403) {
          const body = await response.clone().json().catch(() => ({}));
          if (body?.code === 'PAYMENT_PENDING') {
            if (isStoredSessionCurrent(current)) {
              publishAuthSessionInvalidation(
                window,
                buildAuthSessionInvalidation(current, 'payment_pending')
              );
              terminatePrimarySession('payment_pending');
            } else {
              handleExternalPrimarySessionChange();
            }
            return;
          }
        }

        if (!response.ok) {
          throw new Error('Sessão inválida.');
        }

        const data = await response.json();
        const nextSession = { ...current, user: data.user };
        if (isStoredSessionCurrent(current)) {
          setSession(nextSession);
        } else {
          handleExternalPrimarySessionChange();
        }
      } catch {
        if (isStoredSessionCurrent(current)) {
          publishAuthSessionInvalidation(
            window,
            buildAuthSessionInvalidation(current, 'session_invalid')
          );
          terminatePrimarySession('session_invalid');
        } else {
          handleExternalPrimarySessionChange();
        }
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, [bootstrapSession, terminatePrimarySession]);

  useEffect(() => {
    const handleInvalidSession = (event) => {
      terminatePrimarySession(event?.detail?.reason || 'session_invalid');
    };

    window.addEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession);
    return () => {
      window.removeEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession);
    };
  }, [terminatePrimarySession]);

  useEffect(() => {
    const handleExternalSessionChange = (event) => {
      if (event.key === AUTH_SESSION_INVALIDATION_STORAGE_KEY) {
        if (authSessionMatchesInvalidation(session, event.newValue)) {
          const invalidation = parseAuthSessionInvalidation(event.newValue);
          terminatePrimarySession(invalidation?.reason || 'session_invalid');
        }
        return;
      }

      if (
        event.key !== AUTH_STORAGE_KEY
        || !shouldInvalidateForExternalAuthChange(session, event.newValue)
      ) {
        return;
      }

      handleExternalPrimarySessionChange();
    };

    window.addEventListener('storage', handleExternalSessionChange);
    return () => {
      window.removeEventListener('storage', handleExternalSessionChange);
    };
  }, [session, terminatePrimarySession]);

  const value = useMemo(() => ({
    user: session?.user || null,
    token: session?.token || '',
    isAuthenticated: Boolean(session?.token && session?.user),
    loading,
    login: (nextSession) => {
      purgeCatalogSessionStorage();
      const storedSession = setStoredSession(nextSession);
      setSession(storedSession);
    },
    logout: (options = {}) => {
      announceSessionChanging();
      purgeCatalogSessionStorage();
      clearStoredSession();
      setSession(null);
      window.location.replace(options.redirectTo || '/login');
    },
    updateUser: (user) => {
      if (!session?.token) return;
      const next = { ...session, user };
      if (!isStoredSessionCurrent(session)) {
        handleExternalPrimarySessionChange();
        return;
      }
      setSession(next);
    },
  }), [loading, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
